import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpUnauthorizedError, PayerError } from '@payer-admin-ui/components';
import { toPayerError } from 'utils/rtk';
import { sessionAPI, SessionClient } from './sessionAPI';
import { loadUserSettings } from './userSettings';

type SessionState = {
  client: SessionClient | null;
  expires: string | null;
  isFetching: boolean;
  error: PayerError | null;
};

type SessionPayload = {
  client: SessionClient;
  expires: string | null;
};

const getCurrentSession = createAsyncThunk<Partial<SessionState>, void, { dispatch: any }>(
  'session/current',
  async (_: void, { dispatch }): Promise<Partial<SessionState>> => {
    try {
      const session = await sessionAPI.getCurrent();
      if (session && session.client) {
        dispatch(loadUserSettings());
      }
      return session;
    } catch (error) {
      if (error instanceof HttpUnauthorizedError) {
        return {};
      } else {
        throw error;
      }
    }
  },
);

const logout = createAsyncThunk('session/logout', async (): Promise<unknown> => {
  return sessionAPI.logout();
});

const refresh = createAsyncThunk('session/refresh', async (): Promise<any> => {
  return sessionAPI.refresh();
});

const initialState: SessionState = {
  client: null,
  expires: null,
  isFetching: false,
  error: null,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetError: state => ({ ...state, error: null }),
    setSession: (state, { payload }: { payload: SessionPayload }) => ({
      ...state,
      client: payload.client,
      expires: payload.expires,
    }),
  },
  extraReducers: builder => {
    builder.addCase(getCurrentSession.pending, state => ({
      ...state,
      isFetching: true,
      error: null,
    }));
    builder.addCase(getCurrentSession.fulfilled, (state, { payload }) => ({
      ...initialState,
      ...payload,
      isFetching: false,
    }));
    builder.addCase(getCurrentSession.rejected, (state, { error }) => ({
      ...state,
      isFetching: false,
      error: toPayerError(error),
    }));

    builder.addCase(logout.fulfilled, state => ({
      ...initialState,
    }));

    builder.addCase(refresh.pending, state => ({
      ...state,
      isFetching: true,
      error: null,
    }));
    builder.addCase(refresh.fulfilled, (state, { payload }) => ({
      ...initialState,
      ...payload,
      isFetching: false,
    }));
    builder.addCase(refresh.rejected, (state, { error }) => ({
      ...state,
      isFetching: false,
      error: toPayerError(error),
    }));
  },
});

export { getCurrentSession, logout, refresh };
export const {
  actions: { resetError, setSession },
  reducer: sessionReducer,
} = sessionSlice;
